import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import PageContainer from "src/components/page/PageContainer"

import PasswordChange from "./PasswordChange"
import UpdateLanguageDialog from "./UpdateLanguageDialog"
import { UserContext } from "src/providers"
import {
  Avatar,
  Chip,
  Divider,
  Grid,
  Icon,
  Stack,
  styled,
  Typography,
  useTheme,
  Box,
  TextField,
  Button
} from "@mui/material"
import { ColoredGrid } from "../file/ColoredGridItem"
import { getAvatar, updateProfile } from "src/services/api/user"
import { UserProfile } from "@models/user"
import Footer from "./Footer"
import { useResponsive } from "src/utils/useResponsive"
import UserService from "src/UserService"
import { useSnackbar } from "src/providers/SnackbarProvider"

const ProfilePageChip = styled(Chip)(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: theme.palette.lightSkyBlue.main,
  fontSize: "1rem",
  fontWeight: 500,
  "& .MuiChip-label": { padding: theme.spacing(1, 2) },
  [theme.breakpoints.down("md")]: {
    fontSize: "0.8rem"
  }
}))
ProfilePageChip.defaultProps = { size: "medium", color: "primary" }
export { ProfilePageChip }

const UserInfo = ({ user, imgSrc }: { user: UserProfile; imgSrc?: string }) => {
  const { userCommunities, setUser } = useContext(UserContext)
  const { t } = useTranslation("profile")
  const { palette } = useTheme()
  const { isMobile } = useResponsive()
  const [name, setName] = useState(user?.name)
  const [surname, setSurname] = useState(user?.surname)
  const { snackbarShowMessage } = useSnackbar()
  const [updateLanguageModalOpen, setUpdateLanguageModalOpen] = useState(false)

  const handleUpdateLanguage = () => {
    setUpdateLanguageModalOpen(true)
  }

  return (
    <Stack justifyContent={"space-between"} sx={{ height: "100%" }}>
      <UpdateLanguageDialog
        open={updateLanguageModalOpen}
        isAlreadySetup={false}
        onClose={() => setUpdateLanguageModalOpen(false)}
      />
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box sx={{ display: "flex", pt: { xs: 1, md: 2 } }}>
          <Avatar
            src={imgSrc}
            sx={{
              width: { xs: 80, md: 100 },
              height: { xs: 80, md: 100 },
              alignSelf: "center"
            }}
          >
            <Icon
              sx={{
                fontSize: { xs: "42px", md: "52px" },
                backgroundColor: palette.lightSkyBlue.main,
                borderRadius: "50%",
                padding: { xs: 2.5, md: 3 }
              }}
            >
              <img src={"exfluency/portal-icon_profil.svg"} alt="" style={{ width: "100%" }} />
            </Icon>
          </Avatar>
          <Box sx={{ flex: 1, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              label={t("firstName")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t("lastName")}
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
              fullWidth
              size="small"
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ alignSelf: "flex-start" }}
              onClick={() =>
                updateProfile({
                  name: name,
                  surname: surname,
                  email: user?.email,
                  timezone: UserService.getTokenUserInfo().timezone
                })
                  .then(() => {
                    setUser({ ...user, name: name, surname: surname })
                  })
                  .then(() => {
                    snackbarShowMessage("Profile updated successfully", "info")
                  })
              }
            >
              {t("updateNames")}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mt: 0 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant={isMobile ? "h6" : "h5"}> {t("loggedInAs")} </Typography>
          <ProfilePageChip label={user?.login} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant={isMobile ? "h6" : "h5"}> {t("email")} </Typography>
          <ProfilePageChip label={user?.email} />
        </Box>
        <Divider />
        {!isMobile && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant={isMobile ? "h6" : "h5"}> {t("myOrganization")} </Typography>
            <ProfilePageChip label={user?.company.companyName} />
          </Box>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ flexShrink: 0 }}>{t("myGatedCommunities")} </Typography>
          {userCommunities?.map((community) => (
            <ProfilePageChip key={community.gatedCommunityId} label={community.gatedCommunityName} />
          ))}
        </Box>
        <Typography variant={isMobile ? "h6" : "h5"} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {t("myLanguageSelection.myLanguageSelection")}
          <Button variant="contained" color="primary" sx={{ flex: 1 }} onClick={handleUpdateLanguage}>
            {t("myLanguageSelection.update")}
          </Button>
        </Typography>
      </Stack>
      {!isMobile && <Footer />}
    </Stack>
  )
}

const ProfilePage = () => {
  const { palette } = useTheme()
  const { user } = useContext(UserContext)
  const [imgSrc, setImgSrc] = useState<string>()
  const { isMobile } = useResponsive()
  useEffect(() => {
    user &&
      getAvatar(user.login, "BIG").then((response) => {
        setImgSrc(URL.createObjectURL(response.data))
      })
  }, [])

  return (
    <PageContainer>
      <Grid container sx={{ height: "100%", borderRadius: "inherit" }}>
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: palette.common.white,
            p: { xs: 3, md: 8, lg: 10 }
          }}
        >
          {user && imgSrc && <UserInfo user={user} imgSrc={imgSrc} />}
        </Grid>
        <ColoredGrid
          gridcolor={"lightSkyBlue"}
          item
          xs={12}
          md={6}
          sx={{
            p: { xs: 3, md: 8, lg: 10 },
            borderTopRightRadius: { xs: 0, md: "inherit" },
            borderBottomRightRadius: { xs: 0, md: "inherit" },
            borderTopLeftRadius: { xs: 0, md: 0 },
            borderBottomLeftRadius: { xs: 0, md: 0 }
          }}
        >
          <PasswordChange />
        </ColoredGrid>
        {isMobile ? (
          <Grid
            item
            xs={12}
            sx={{
              backgroundColor: palette.common.white,
              p: { xs: 3, md: 8, lg: 10 }
            }}
          >
            <Footer />
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </PageContainer>
  )
}

export default ProfilePage
